import { isNull, isUndefined } from 'lodash-es'

import { LOCAL_STORAGE_KEY_SEPARATOR, NAMESPACE } from '$constants'

type StoredValue = ReturnType<typeof localStorage.getItem>

const safeStorage = <T>(cb: (storage: typeof localStorage) => T) => {
  if (!localStorage) return undefined

  return cb(localStorage)
}

abstract class Storage<T> {
  key = NAMESPACE

  constructor(names: string[]) {
    this.key = [NAMESPACE, ...names].join(LOCAL_STORAGE_KEY_SEPARATOR)
  }

  protected abstract parse<D>(data: StoredValue, defaultValue: D): T | D

  public get = (defaultValue: T) => {
    const v = safeStorage(s => {
      const data = s.getItem(this.key)

      return this.parse(data, defaultValue)
    })

    return v || defaultValue
  }

  public watch = (callback: (newData: T) => void) => {
    const onChange = (ev: StorageEvent) => {
      const keyMatch = ev.key === this.key
      const isUnchanged = ev.oldValue === ev.newValue

      if (!keyMatch || isUnchanged) return

      const newData = this.parse(ev.newValue, undefined)

      if (isUndefined(newData)) return

      callback(newData)
    }

    self.addEventListener('storage', onChange)

    return () => {
      self.removeEventListener('storage', onChange)
    }
  }
}

export class PrimitiveStorage<T> extends Storage<T> {
  // To store as object. E.g: {value: 123}
  private accessor = 'value'

  protected parse = <D>(data: StoredValue, defaultValue: D) => {
    if (isNull(data)) return defaultValue

    const decoded = JSON.parse(data)
    const value = decoded[this.accessor]

    if (!value) return defaultValue

    return value as T
  }

  public set = (value: T) => {
    const encoded = JSON.stringify({
      [this.accessor]: value
    })

    safeStorage(s => s.setItem(this.key, encoded))
  }
}

export class ObjectStorage<T extends object> extends Storage<T> {
  protected parse = <D>(data: StoredValue, defaultValue: D) => {
    if (isNull(data)) return defaultValue

    return JSON.parse(data) as T
  }

  public set = (value: T) => {
    safeStorage(s => s.setItem(this.key, JSON.stringify(value)))
  }
}
